import React, { useEffect, useRef, useState, useContext } from "react";
import axios from "axios";
import {
  Send,
  Sparkles,
  RefreshCw,
  Maximize2,
  Minimize2,
  X,
} from "lucide-react";

import { AuthContext } from "../context/AuthContext";
import ChatMessage from "./ChatMessage";

const ChatBot = ({ isDrawer = false, onClose }) => {
  const { authData, backUrl } = useContext(AuthContext);

  const currentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const [messages, setMessages] = useState([
    {
      sender: "ai",
      title: "New Chat Session Initialized ✨",
      text: "How may I assist you today? You can inquire about attendance, apply for leaves or permissions, check balances, or ask any workplace policy questions.",
      display_type: "text",
      time: currentTime(),
    },
  ]);

  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [userScope, setUserScope] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const messagesEndRef = useRef(null);

  // Thread isolation
  const [threadId, setThreadId] = useState(() => {
    const existing = localStorage.getItem("ai_chat_thread_id");
    if (existing) return existing;
    const id = "ai_thread_" + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
    localStorage.setItem("ai_chat_thread_id", id);
    return id;
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleResetSession = () => {
    const newId = "ai_thread_" + (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2));
    localStorage.setItem("ai_chat_thread_id", newId);
    setThreadId(newId);
    setMessages([
      {
        sender: "ai",
        title: "New Chat Session Initialized ✨",
        text: "How may I assist you today? Previous context has been reset and memory is fresh.",
        display_type: "text",
        time: currentTime(),
      },
    ]);
  };

  const getAuthHeaders = () => {
    let token = authData?.access || authData?.token || authData?.access_token;
    if (!token) {
      try {
        const stored = JSON.parse(localStorage.getItem("authData") || "{}");
        token = stored?.access || stored?.token || stored?.access_token;
      } catch (e) {
        token = null;
      }
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getBaseUrl = () => {
    return backUrl !== undefined && backUrl !== null ? backUrl : "";
  };

  const sendMessage = async (customQuery) => {
    const queryToSend = customQuery || question;
    if (!queryToSend.trim()) return;

    const userMessage = {
      sender: "user",
      text: queryToSend,
      time: currentTime(),
    };

    setMessages((prev) => [...prev, userMessage]);
    if (!customQuery) setQuestion("");
    setLoading(true);

    try {
      const baseUrl = getBaseUrl();
      const headers = getAuthHeaders();

      const response = await axios.post(
        `${baseUrl}/api/chatbot/ai-chat/`,
        {
          question: queryToSend,
          thread_id: threadId,
        },
        { headers }
      );

      if (response.data.scope_info) {
        setUserScope(response.data.scope_info);
      }

      const aiMessage = {
        sender: "ai",
        text: response.data.answer,
        display_type: response.data.display_type || "text",
        data: response.data.data || {},
        sources: response.data.sources || [],
        tool_called: response.data.tool_called,
        time: currentTime(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("AI Assistant Chat Error:", error);
      // Fallback attempt to chat endpoint
      let handled = false;
      try {
        const baseUrl = getBaseUrl();
        const headers = getAuthHeaders();
        const fbRes = await axios.post(
          `${baseUrl}/api/chatbot/chat/`,
          {
            question: queryToSend,
            thread_id: threadId,
          },
          { headers }
        );
        if (fbRes.data && fbRes.data.success !== false && fbRes.data.answer) {
          setMessages((prev) => [
            ...prev,
            {
              sender: "ai",
              text: fbRes.data.answer,
              sources: fbRes.data.sources || [],
              display_type: "text",
              time: currentTime(),
            },
          ]);
          handled = true;
        }
      } catch (fbErr) {
        // Fallback failed
      }

      if (!handled) {
        const errorMsg =
          error.response?.data?.message ||
          error.response?.data?.detail ||
          error.message ||
          "Unable to connect to the AI Assistant server. Please verify you are logged in.";
        setMessages((prev) => [
          ...prev,
          {
            sender: "ai",
            text: `⚠️ ${errorMsg}`,
            display_type: "error",
            time: currentTime(),
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const promptChips = [
    "My attendance today",
    "What are my leave balances?",
    "Apply leave tomorrow for fever",
    "Apply 2 hours permission today",
    "Team attendance",
    "Show holiday calendar",
    "What is the company leave policy?",
  ];

  return (
    <div
      style={{
        maxWidth: isDrawer ? "100%" : isMaximized ? "1100px" : "640px",
        width: "100%",
        margin: isDrawer ? "0" : "20px auto",
        borderRadius: isDrawer ? "0px" : "24px",
        overflow: "hidden",
        background: "linear-gradient(180deg, #f8fafc 0%, #f0f7ff 50%, #e6f0fa 100%)",
        color: "#0f172a",
        boxShadow: isDrawer
          ? "none"
          : "0 20px 50px -10px rgba(14, 73, 123, 0.22), 0 0 0 1px rgba(14, 73, 123, 0.15)",
        border: isDrawer ? "none" : "1px solid rgba(14, 73, 123, 0.2)",
        display: "flex",
        flexDirection: "column",
        height: isDrawer ? "100%" : isMaximized ? "90vh" : "740px",
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        position: "relative",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ── HEADER (CORPORATE BLUE THEME) ── */}
      <div
        style={{
          padding: "16px 20px",
          background: "linear-gradient(135deg, #0e497b 0%, #1e5c94 50%, #10385c 100%)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 4px 16px rgba(14, 73, 123, 0.28)",
          borderTopLeftRadius: isDrawer ? "0px" : "24px",
          borderTopRightRadius: isDrawer ? "0px" : "24px",
        }}
      >
        {/* Left: Avatar + Title + Status */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Bot Avatar Squircle with Live Dot */}
          <div
            style={{
              position: "relative",
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              background: "#082138",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
              flexShrink: 0,
            }}
          >
            {/* Robot Face SVG */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="4" y="8" width="16" height="12" rx="4" />
              <circle cx="9" cy="13" r="1.2" fill="#ffffff" />
              <circle cx="15" cy="13" r="1.2" fill="#ffffff" />
              <path d="M10 17h4" />
              <path d="M12 4v4" />
              <circle cx="12" cy="3" r="1" fill="#38bdf8" stroke="#38bdf8" />
              <path d="M2 13h2" />
              <path d="M20 13h2" />
            </svg>

            {/* Live Green Status Indicator Dot */}
            <span
              style={{
                position: "absolute",
                bottom: "-2px",
                right: "-2px",
                width: "11px",
                height: "11px",
                borderRadius: "50%",
                background: "#10b981",
                border: "2px solid #0e497b",
                boxShadow: "0 0 6px rgba(16, 185, 129, 0.7)",
              }}
            />
          </div>

          <div>
            <div
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "19px",
                fontWeight: "700",
                color: "#ffffff",
                letterSpacing: "0.2px",
                lineHeight: "1.2",
              }}
            >
              AI Assistant
            </div>

            <div
              style={{
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginTop: "3px",
                color: "#e0f2fe",
                fontWeight: "500",
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: "#34d399",
                  display: "inline-block",
                  boxShadow: "0 0 6px #34d399",
                }}
              />
              <span>Online & Ready</span>

              {userScope && (
                <span
                  style={{
                    marginLeft: "4px",
                    padding: "1px 7px",
                    borderRadius: "6px",
                    fontWeight: "600",
                    fontSize: "10.5px",
                    background: "rgba(255, 255, 255, 0.2)",
                    color: "#ffffff",
                    backdropFilter: "blur(4px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  {userScope.role === "ADMIN" || userScope.is_hr_admin || userScope.role_tier === "HR_ADMIN"
                    ? "HR Admin (All Employees)"
                    : userScope.is_hod || userScope.role_tier === "HOD"
                    ? `HOD Scope (${userScope.accessible_count})`
                    : userScope.is_tl || userScope.role_tier === "TL"
                    ? `Team Lead (${userScope.accessible_count})`
                    : "Self Scope"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Header Control Buttons */}
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* Refresh / Reset Session */}
          <button
            onClick={handleResetSession}
            title="Start New Chat Session"
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "8px",
              background: "rgba(255, 255, 255, 0.18)",
              border: "1px solid rgba(255, 255, 255, 0.28)",
              color: "#ffffff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.28)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)")}
          >
            <RefreshCw size={15} />
          </button>

          {/* Maximize / Restore Toggle */}
          {!isDrawer && (
            <button
              onClick={() => setIsMaximized((prev) => !prev)}
              title={isMaximized ? "Restore Size" : "Maximize Chat"}
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "8px",
                background: "rgba(255, 255, 255, 0.18)",
                border: "1px solid rgba(255, 255, 255, 0.28)",
                color: "#ffffff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.28)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)")}
            >
              {isMaximized ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}

          {/* Close Assistant */}
          {onClose && (
            <button
              onClick={onClose}
              title="Close Assistant"
              style={{
                width: "34px",
                height: "34px",
                borderRadius: "8px",
                background: "rgba(255, 255, 255, 0.18)",
                border: "1px solid rgba(255, 255, 255, 0.28)",
                color: "#ffffff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.28)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.18)")}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* ── MESSAGES CONTAINER ── */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 20px 10px 20px",
          background: "transparent",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {messages.map((message, index) => (
          <ChatMessage key={index} message={message} />
        ))}

        {loading && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              color: "#0e497b",
              fontSize: "13px",
              fontWeight: "600",
              padding: "10px 18px",
              borderRadius: "18px",
              background: "rgba(255, 255, 255, 0.95)",
              border: "1px solid rgba(14, 73, 123, 0.25)",
              boxShadow: "0 4px 16px rgba(14, 73, 123, 0.1)",
              backdropFilter: "blur(12px)",
              maxWidth: "340px",
              marginBottom: "16px",
              alignSelf: "flex-start",
            }}
          >
            <Sparkles size={16} color="#0284c7" className="animate-spin" />
            <span>AI Assistant is reasoning...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── SUGGESTED QUICK PROMPT CHIPS ── */}
      <div
        style={{
          padding: "8px 18px",
          background: "rgba(240, 247, 255, 0.85)",
          backdropFilter: "blur(10px)",
          borderTop: "1px solid rgba(14, 73, 123, 0.15)",
          display: "flex",
          gap: "8px",
          overflowX: "auto",
          whiteSpace: "nowrap",
          scrollbarWidth: "none",
        }}
      >
        {promptChips.map((chip, idx) => (
          <button
            key={idx}
            onClick={() => sendMessage(chip)}
            disabled={loading}
            style={{
              padding: "5px 13px",
              borderRadius: "18px",
              background: "rgba(255, 255, 255, 0.95)",
              border: "1px solid rgba(14, 73, 123, 0.25)",
              color: "#0e497b",
              fontSize: "11.5px",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(14, 73, 123, 0.06)",
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#e0f2fe";
              e.currentTarget.style.borderColor = "#0e497b";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.95)";
              e.currentTarget.style.borderColor = "rgba(14, 73, 123, 0.25)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            {chip}
          </button>
        ))}
      </div>

      {/* ── DOCKED INPUT FOOTER (BLUE THEME) ── */}
      <div
        style={{
          padding: "12px 20px 14px 20px",
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderTop: "1px solid rgba(14, 73, 123, 0.18)",
          borderBottomLeftRadius: isDrawer ? "0px" : "24px",
          borderBottomRightRadius: isDrawer ? "0px" : "24px",
        }}
      >
        {/* Pill Container */}
        <div
          style={{
            border: "1.5px solid #0e497b",
            borderRadius: "9999px",
            background: "#ffffff",
            boxShadow: "0 2px 10px rgba(14, 73, 123, 0.08)",
            display: "flex",
            alignItems: "center",
            padding: "4px 6px 4px 18px",
            gap: "8px",
            transition: "all 0.2s ease",
          }}
        >
          <input
            type="text"
            placeholder="Ask your attendance, leave balances, salary, or policies..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: "13.5px",
              color: "#0f172a",
              fontFamily: "inherit",
            }}
          />

          {/* Blue Circular Send Button with Paper Plane */}
          <button
            onClick={() => sendMessage()}
            disabled={loading || !question.trim()}
            title="Send query"
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "50%",
              background: loading || !question.trim()
                ? "rgba(14, 73, 123, 0.35)"
                : "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: loading || !question.trim() ? "not-allowed" : "pointer",
              color: "#ffffff",
              boxShadow: loading || !question.trim() ? "none" : "0 3px 10px rgba(14, 73, 123, 0.35)",
              transition: "transform 0.15s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!loading && question.trim()) {
                e.currentTarget.style.transform = "scale(1.06)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Send size={16} style={{ transform: "translateX(1px)" }} />
          </button>
        </div>

        {/* Footer Helper Text */}
        <div
          style={{
            fontSize: "11.5px",
            color: "#64748b",
            textAlign: "center",
            marginTop: "8px",
            fontWeight: "500",
          }}
        >
          Press <strong style={{ color: "#0e497b" }}>Enter ↵</strong> to send • <strong style={{ color: "#0e497b" }}>Shift + Enter</strong> for newline
        </div>
      </div>
    </div>
  );
};

export default ChatBot;